import { defaultSynthMember } from "pages/Space/constants/defaultSynthMember";
import { Space, SpaceGuest, SpaceMember } from "redux/Ldb/types/spaceTypes";

export const normalizeMembers = (
    spaceData: Space,
    { includeSynth = true }: { includeSynth?: boolean } = {}
) => {
    return spaceData?.members
        .map((member) => {
            const spaceMember = member as unknown as SpaceMember;
            const spaceGuest = member as unknown as SpaceGuest;
            return {
                ldbId: spaceMember?.ldbId,
                guestId: spaceGuest?.guestId,
                avatar: spaceMember?.ldb?.profile?.avatar,
                businessName: spaceMember?.ldb?.profile?.businessName,
                role: spaceMember?.ldb?.role || spaceGuest?.guest?.role,
                fullName:
                    spaceMember?.ldb?.fullName ||
                    [spaceGuest?.guest?.firstName, spaceGuest?.guest?.lastName].join(" ").trim(),
                email: spaceMember?.ldb?.email || spaceGuest?.guest?.email,
                isGuest: !!spaceGuest?.guestId,
                isOwner: spaceMember?.isOwner || false,
                isSynth: false,
            };
        })
        .concat(
            includeSynth
                ? {
                      ldbId: "",
                      role: defaultSynthMember.email,
                      guestId: undefined,
                      avatar: defaultSynthMember.image,
                      businessName: "",
                      fullName: defaultSynthMember.name,
                      email: defaultSynthMember.email,
                      isGuest: false,
                      isOwner: false,
                      isSynth: true,
                  }
                : []
        );
};

/**
 * Calculate unread message count for a space based on local read tracking
 * This is a temporary solution until backend implements proper unread tracking
 */
export const calculateUnreadCount = (spaceId: string, totalMessages: number, currentUserId: string): number => {
    if (!spaceId || !currentUserId || currentUserId === "unknown-user") {
        return 0;
    }

    try {
        const readMessagesKey = `space_read_messages_${spaceId}_${currentUserId}`;
        const readMessages = JSON.parse(localStorage.getItem(readMessagesKey) || '[]');
        
        // Calculate unread count as total messages minus read messages
        const unreadCount = Math.max(0, totalMessages - readMessages.length);
        
        return unreadCount;
    } catch (error) {
        console.error('Error calculating unread count:', error);
        return 0;
    }
};

/**
 * Mark a message as read locally
 */
export const markMessageAsReadLocally = (spaceId: string, messageId: string, currentUserId: string): void => {
    if (!spaceId || !messageId || !currentUserId || currentUserId === "unknown-user") {
        return;
    }

    try {
        const readMessagesKey = `space_read_messages_${spaceId}_${currentUserId}`;
        const existingReadMessages = JSON.parse(localStorage.getItem(readMessagesKey) || '[]');
        
        if (!existingReadMessages.includes(messageId)) {
            const updatedReadMessages = [...existingReadMessages, messageId];
            localStorage.setItem(readMessagesKey, JSON.stringify(updatedReadMessages));
        }
    } catch (error) {
        console.error('Error marking message as read locally:', error);
    }
};

/**
 * Get all read message IDs for a space
 */
export const getReadMessageIds = (spaceId: string, currentUserId: string): string[] => {
    if (!spaceId || !currentUserId || currentUserId === "unknown-user") {
        return [];
    }

    try {
        const readMessagesKey = `space_read_messages_${spaceId}_${currentUserId}`;
        return JSON.parse(localStorage.getItem(readMessagesKey) || '[]');
    } catch (error) {
        console.error('Error getting read message IDs:', error);
        return [];
    }
};
